/**
 * Deploy & Build Service
 *
 * Maps IDE compile/deploy/test operations to workspace-service endpoints.
 * All commands run inside the workspace container — real compute, not simulation.
 *
 * Workspace-service endpoints (all under /api/ide/workspaces/:id):
 *   POST /compile      — Compile Solidity contracts
 *   POST /deploy       — Deploy a contract
 *   POST /test         — Run unit tests
 *   POST /exec         — Run sandboxed custom command
 *   POST /install      — Install npm dependencies
 *   POST /init-foundry — Initialize Foundry toolchain
 *   GET  /toolchain    — Get toolchain info
 *   POST /anvil        — Start local Anvil devnet
 */

import { get, post } from '@/lib/api';
import type { CompileResult, CompileError, DeployResult } from '@/types';

/* ================================================================ */
/*  Compile                                                         */
/* ================================================================ */

/** Raw shape returned by workspace-service compileContracts */
interface BackendCompileResult {
  success: boolean;
  stdout: string;
  stderr: string;
  command: string[];
  toolchain: string;
  durationMs: number;
  artifacts?: string[];
  errors?: { file: string; line: number; column: number; severity: 'error' | 'warning'; message: string }[];
}

/** Normalize backend compile result into frontend CompileResult */
function normalizeCompileResult(raw: BackendCompileResult): CompileResult {
  const errors: CompileError[] = [];
  const warnings: CompileError[] = [];

  if (raw.errors) {
    for (const e of raw.errors) {
      const item: CompileError = {
        severity: e.severity,
        message: e.message,
        sourceLocation: { file: e.file, start: e.line, end: e.column },
      };
      if (e.severity === 'warning') warnings.push(item);
      else errors.push(item);
    }
  }

  // If no parsed errors but compilation failed, create an error from stderr
  if (!raw.success && errors.length === 0 && raw.stderr) {
    errors.push({
      severity: 'error',
      message: raw.stderr.split('\n').filter(Boolean).slice(0, 5).join('\n'),
    });
  }

  return {
    success: raw.success,
    errors,
    warnings,
    stdout: raw.stdout,
    stderr: raw.stderr,
    artifacts: raw.artifacts,
    toolchain: raw.toolchain,
    durationMs: raw.durationMs,
    // Derive a rough gas estimate from bytecode size in stdout/artifacts where available.
    // Deployment gas ≈ (bytecodeBytes × 200) + 32_000 base cost.
    gasEstimate: estimateGasFromOutput(raw),
  };
}

/**
 * Derives a rough deployment gas estimate from the compilation output.
 * Looks for bytecode size in stdout (Foundry format) or falls back to
 * a heuristic based on artifact count.
 */
function estimateGasFromOutput(raw: BackendCompileResult): string | undefined {
  // Foundry gas estimation is typically in the test output, not compile.
  // Try to parse "Deployment cost: NNN gas" or similar lines from stdout.
  if (raw.stdout) {
    const match = raw.stdout.match(/(?:deployment.*?|deploy.*?|gas used.*?)(\d[\d,_]+)\s*gas/i);
    if (match) {
      const gasNum = parseInt(match[1].replace(/[,_]/g, ''), 10);
      if (!isNaN(gasNum) && gasNum > 0) return formatGas(gasNum);
    }
    // Foundry format e.g. "Gas used: 1234567"
    const gasUsedMatch = raw.stdout.match(/Gas\s+(?:used|estimate)[:\s]+(\d[\d,_]+)/i);
    if (gasUsedMatch) {
      const gasNum = parseInt(gasUsedMatch[1].replace(/[,_]/g, ''), 10);
      if (!isNaN(gasNum) && gasNum > 0) return formatGas(gasNum);
    }
  }
  return undefined;
}

function formatGas(gas: number): string {
  if (gas >= 1_000_000) return `${(gas / 1_000_000).toFixed(2)}M`;
  if (gas >= 1_000) return `${(gas / 1_000).toFixed(1)}k`;
  return String(gas);
}

export async function compile(
  workspaceId: string,
  toolchain?: 'hardhat' | 'foundry',
): Promise<CompileResult> {
  const raw = await post<BackendCompileResult>(
    `/ide/workspaces/${workspaceId}/compile`,
    toolchain ? { toolchain } : {},
  );
  return normalizeCompileResult(raw);
}

/* ================================================================ */
/*  Deploy                                                          */
/* ================================================================ */

export interface DeployOptions {
  toolchain?: 'hardhat' | 'foundry';
  script?: string;
  contract?: string;
  network?: string;
  constructorArgs?: string[];
  rpcUrl?: string;
  privateKey?: string;
}

export async function deploy(
  workspaceId: string,
  options: DeployOptions = {},
): Promise<DeployResult> {
  return post<DeployResult>(
    `/ide/workspaces/${workspaceId}/deploy`,
    options,
  );
}

/* ================================================================ */
/*  Test                                                            */
/* ================================================================ */

export interface TestOptions {
  toolchain?: 'hardhat' | 'foundry';
  testFile?: string;
  grep?: string;
  gasReport?: boolean;
  verbosity?: number;
}

export interface TestResult {
  success: boolean;
  output: string;
  passed: number;
  failed: number;
  skipped: number;
  gasReport?: string;
}

/** Raw shape returned by workspace-service runTests */
interface BackendTestResult {
  success: boolean;
  stdout: string;
  stderr: string;
  command: string[];
  toolchain: string;
  durationMs: number;
  passed: number;
  failed: number;
  total: number;
  gasReport?: string;
}

export async function runTests(
  workspaceId: string,
  options: TestOptions = {},
): Promise<TestResult> {
  const raw = await post<BackendTestResult>(
    `/ide/workspaces/${workspaceId}/test`,
    options,
  );
  return {
    success: raw.success,
    output: raw.stdout + (raw.stderr ? '\n' + raw.stderr : ''),
    passed: raw.passed,
    failed: raw.failed,
    skipped: Math.max(0, raw.total - raw.passed - raw.failed),
    gasReport: raw.gasReport,
  };
}

/* ================================================================ */
/*  Custom Command Execution                                        */
/* ================================================================ */

export interface ExecResult {
  success: boolean;
  output: string;
  exitCode: number;
}

export async function exec(
  workspaceId: string,
  command: string[],
): Promise<ExecResult> {
  return post<ExecResult>(
    `/ide/workspaces/${workspaceId}/exec`,
    { command },
  );
}

/* ================================================================ */
/*  Package Management                                              */
/* ================================================================ */

export async function installDependencies(
  workspaceId: string,
): Promise<ExecResult> {
  return post<ExecResult>(
    `/ide/workspaces/${workspaceId}/install`,
  );
}

/* ================================================================ */
/*  Foundry                                                         */
/* ================================================================ */

export async function initFoundry(
  workspaceId: string,
): Promise<ExecResult> {
  return post<ExecResult>(
    `/ide/workspaces/${workspaceId}/init-foundry`,
  );
}

/* ================================================================ */
/*  Toolchain Info                                                  */
/* ================================================================ */

export interface ToolchainInfo {
  hardhat: boolean;
  foundry: boolean;
  solc: string | null;
  hardhatVersion: string | null;
  forgeVersion: string | null;
}

/** Raw shape returned by workspace-service getToolchainInfo */
interface BackendToolchainInfo {
  hasHardhat: boolean;
  hasFoundry: boolean;
  solcVersion: string | null;
  hardhatVersion?: string | null;
  forgeVersion?: string | null;
}

export async function getToolchainInfo(
  workspaceId: string,
): Promise<ToolchainInfo> {
  const raw = await get<BackendToolchainInfo>(
    `/ide/workspaces/${workspaceId}/toolchain`,
  );
  return {
    hardhat: raw.hasHardhat,
    foundry: raw.hasFoundry,
    solc: raw.solcVersion ?? null,
    hardhatVersion: raw.hardhatVersion ?? null,
    forgeVersion: raw.forgeVersion ?? null,
  };
}

/* ================================================================ */
/*  Anvil (Local Devnet)                                            */
/* ================================================================ */

export interface AnvilOptions {
  port?: number;
  forkUrl?: string;
  blockTime?: number;
}

export interface AnvilResult {
  success: boolean;
  rpcUrl: string;
  accounts: string[];
  chainId: number;
}

export async function startAnvil(
  workspaceId: string,
  options: AnvilOptions = {},
): Promise<AnvilResult> {
  return post<AnvilResult>(
    `/ide/workspaces/${workspaceId}/anvil`,
    options,
  );
}
